import { useQuery } from '@apollo/client';
import { useMemo } from 'react';
import { userFreeTrialStatusQuery } from '../graphql-operations/UserIdentity.graphql';
import useCartHasFreeTrialBundle from './useCartHasFreeTrialBundle';
import useFreeTrialEnabled from './useFreeTrialEnabled';

interface UserFreeTrialStatus {
  currentUser?: {
    userHouseholds?: Array<{
      household?: {
        pets?: Array<{
          device?: {
            hasActiveSubscription?: boolean | null;
            subscription?: {
              hasFreeTrialPlan?: boolean | null;
            } | null;
          } | null;
        }>;
      };
    }>;
  };
}

/**
 * Returns false when the trial is not on offer (flag off, or the visitor is in the
 * hardware-first arm), a trial is already in cart, the user has an active trial
 * subscription on any device, or the user is an existing member - existing members
 * can't start a free trial.
 */
export default function useFreeTrialPromoVisible(): boolean {
  const isFreeTrialEnabled = useFreeTrialEnabled();
  const hasFreeTrialInCart = useCartHasFreeTrialBundle();

  const { data: freeTrialData } = useQuery<UserFreeTrialStatus>(userFreeTrialStatusQuery, {
    skip: !isFreeTrialEnabled || hasFreeTrialInCart,
  });

  const hasExistingFreeTrial = useMemo(() => {
    const households = freeTrialData?.currentUser?.userHouseholds ?? [];
    return households.some((h) => h.household?.pets?.some((p) => p.device?.subscription?.hasFreeTrialPlan === true));
  }, [freeTrialData]);

  const hasActiveMembership = useMemo(() => {
    const households = freeTrialData?.currentUser?.userHouseholds ?? [];
    return households.some((h) => h.household?.pets?.some((p) => p.device?.hasActiveSubscription === true));
  }, [freeTrialData]);

  return isFreeTrialEnabled && !hasFreeTrialInCart && !hasExistingFreeTrial && !hasActiveMembership;
}
